import { cn } from "@/lib/utils";

interface SkeletonProps {
  className?: string;
}

export function Skeleton({ className }: SkeletonProps) {
  return <div className={cn("skeleton", className)} />;
}

/**
 * Meniru anatomi BentoStatCard persis (min-h-[120px], aksen kiri 3px, ikon 14px
 * di baris atas, angka besar di bawah). Versi lama memakai ikon 40px tanpa tinggi
 * minimum, jadi bentuknya sama sekali tidak menyerupai kartu aslinya dan garis
 * aksen berwarna "muncul tiba-tiba" saat data masuk.
 */
export function StatCardSkeleton() {
  return (
    <div className="relative flex min-h-[120px] flex-col justify-between rounded-[var(--radius-lg,12px)] border border-[rgba(0,0,0,0.06)] border-l-[3px] border-l-[rgba(0,0,0,0.10)] bg-white p-5 shadow-[var(--shadow-card)]">
      <div className="flex items-center gap-1.5">
        <Skeleton className="h-3.5 w-3.5 rounded" />
        <Skeleton className="h-2.5 w-20 rounded" />
      </div>
      <div className="mt-3">
        <Skeleton className="h-9 w-28 rounded" />
      </div>
    </div>
  );
}

/**
 * Kerangka halaman generik untuk `loading.tsx` per grup rute — header + baris
 * stat + satu blok konten. Sebelumnya tidak ada satu pun loading.tsx, jadi tiap
 * navigasi menampilkan layar kosong sampai fetch klien selesai.
 */
export function PageSkeleton({ stats = 4 }: { stats?: number }) {
  return (
    <div className="space-y-6" role="status" aria-label="Memuat halaman">
      <div className="space-y-2">
        <Skeleton className="h-8 w-56 rounded-lg" />
        <Skeleton className="h-3.5 w-72 rounded" />
      </div>
      {stats > 0 && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {Array.from({ length: stats }).map((_, i) => (
            <StatCardSkeleton key={i} />
          ))}
        </div>
      )}
      <div className="rounded-[var(--radius-lg,12px)] bg-white p-5 shadow-[var(--shadow-card)]">
        <Skeleton className="mb-4 h-5 w-40 rounded" />
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      </div>
      <span className="sr-only">Memuat…</span>
    </div>
  );
}

export function CardSkeleton() {
  return (
    <div className="glass-card p-5">
      <div className="skeleton h-8 w-8 rounded-lg mb-3" />
      <div className="skeleton h-3 w-20 mb-2 rounded" />
      <div className="skeleton h-6 w-28 rounded" />
    </div>
  );
}

export function TableRowSkeleton({ cols = 5, rows = 5 }: { cols?: number; rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i}>
          {Array.from({ length: cols }).map((_, j) => (
            <td key={j} className="px-4 py-3">
              <div className="skeleton h-4 rounded w-full" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
